import { useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';
import type { IframePoolOrderItem } from '../../../../modules/api';
import {
  CARD_FRAME_STYLE,
  FmvOnly,
  SLEEK_RESULT_HOLD_MS,
  SleekCardTitle,
  SleekRarityMarks,
  SoldLine,
  WinnerCard,
  sellLabel,
  stageReserves,
} from '../../../../modules/reveal';
import { SleekButton } from '../../../../modules/theme';
import { SLEEK_PAGE_HEIGHT, SleekPageHeader } from '../../shared';

// The reveal's resolved rise (SleekRevealBottomPanel), so a sale reads the
// same in both places.
const RESULT_HIDDEN = { y: 60, opacity: 0 };
const RESULT_SHOWN = { y: 0, opacity: 1 };
const RESULT_RISE = { duration: 0.5, ease: [0.16, 1, 0.3, 1] } as const;

type Props = {
  item: IframePoolOrderItem;
  onBack: () => void;
  /** Sells the card; resolves to whether it sold. */
  onSell: (item: IframePoolOrderItem) => Promise<boolean>;
  /** The sale's result has been on screen long enough — leave the page. */
  onSold: () => void;
  /** Starts shipping this card; omitted when the operator disabled shipping. */
  onShip?: (item: IframePoolOrderItem) => void;
};

/**
 * One Vault card, laid out exactly like the card on the reveal (partner
 * 2026-10-12: "the same exact layout and styling"): the reveal's top and
 * bottom bands, its card frame with the rarity chip and glow, the title and
 * fair market value, then "Ship" where the reveal has "Vault" beside
 * "Sell • N% Value". The bands mirror WinnerContent's — change them together.
 * Opened from a Vault row; selling or shipping ends here. A sale swaps the
 * value and buttons for "+$X Added to your wallet", holds it, then goes back
 * to the Vault (partner Figma 2026-10-15) — as the reveal's last pack does.
 */
export function VaultCardPage({ item, onBack, onSell, onSold, onShip }: Props) {
  const [selling, setSelling] = useState(false);
  const [isSold, setIsSold] = useState(false);
  const holdTimerRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (holdTimerRef.current !== null) window.clearTimeout(holdTimerRef.current);
    },
    [],
  );
  const gross = item.bestActiveOffer?.grossPrice ?? 0;
  const canSell = gross > 0 && Boolean(item.poolOrderId);
  const fmv = item.fairMarketValue;
  const reserves = stageReserves(true);

  const handleSell = async () => {
    if (!canSell || selling) return;
    setSelling(true);
    try {
      if (await onSell(item)) setIsSold(true);
    } finally {
      setSelling(false);
    }
  };

  const leaveAfterResult = () => {
    if (holdTimerRef.current !== null) return;
    holdTimerRef.current = window.setTimeout(onSold, SLEEK_RESULT_HOLD_MS);
  };

  return (
    // `overflow-x-clip`: the rarity glow is wider than a phone and would widen
    // the document, making mobile browsers zoom out and scroll both ways.
    <div data-page="vault-card" className={`@container relative flex ${SLEEK_PAGE_HEIGHT} w-full flex-col items-center overflow-x-clip`}>
      {/* Floats over the top band, like the reveal's close button. */}
      <div className="absolute inset-x-0 top-0 z-30">
        <SleekPageHeader onBack={onBack} isOpaque={false} />
      </div>
      <div
        className="w-full shrink-0 pt-16 @[900px]:[@media(min-height:700px)]:pt-25 pb-2"
        style={{ minHeight: reserves.top }}
      />
      <div className="relative grow min-h-0 w-full flex items-center justify-center">
        <div className="relative" style={CARD_FRAME_STYLE}>
          <SleekRarityMarks badgeName={item.badge?.name} />
          <WinnerCard item={item} />
        </div>
      </div>
      <div className="w-full shrink-0 flex flex-col items-center px-5 pt-5 pb-6" style={{ minHeight: reserves.bottom }}>
        <SleekCardTitle title={item.title} />
        <div className="w-full max-w-[480px] mx-auto flex flex-1 flex-col items-center justify-between gap-6">
          {isSold ? (
            <motion.div initial={RESULT_HIDDEN} animate={RESULT_SHOWN} transition={RESULT_RISE} onAnimationComplete={leaveAfterResult}>
              <SoldLine amount={gross} />
            </motion.div>
          ) : (
            <>
              {fmv != null && fmv > 0 && <FmvOnly fmv={fmv} roll={false} instant />}
              <div className="flex w-full gap-3">
                {onShip && (
                  <SleekButton name="MyPacksShipButton" onClick={() => onShip(item)} disabled={selling} className="w-[100px] shrink-0">
                    Ship
                  </SleekButton>
                )}
                <SleekButton name="MyPacksSellButton" onClick={() => void handleSell()} disabled={!canSell || selling} className="flex-1">
                  {selling ? 'Selling…' : sellLabel(gross, fmv)}
                </SleekButton>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
